import { useState } from "react";
import { OriginalDialog } from "./OriginalDialog";

import type { PlayerCount } from "@marshmallow-duel/simulation";
import type { LocalFighter } from "../game/local-roster";
import type { TranslationKey } from "../i18n";

interface PlayerEditorProps {
  readonly mode: "select" | "edit";
  readonly playerCount: PlayerCount;
  readonly fighters: readonly LocalFighter[];
  readonly selection: readonly string[];
  readonly t: (key: TranslationKey) => string;
  readonly onFightersChange: (fighters: LocalFighter[]) => void;
  readonly onSelectionChange: (selection: string[]) => void;
  readonly onClose: () => void;
}

const cssColor = (color: number) => {
  const red = color & 0xff;
  const green = (color >> 8) & 0xff;
  const blue = (color >> 16) & 0xff;
  return `#${red.toString(16).padStart(2, "0")}${green.toString(16).padStart(2, "0")}${blue.toString(16).padStart(2, "0")}`;
};

const gmColor = (color: string) => Number.parseInt(`${color.slice(5, 7)}${color.slice(3, 5)}${color.slice(1, 3)}`, 16);

export const PlayerEditor = ({ mode, playerCount, fighters, selection, t, onFightersChange, onSelectionChange, onClose }: PlayerEditorProps) => {
  const [selected, setSelected] = useState(() => selection.slice(0, playerCount));
  const updateFighter = (id: string, patch: Partial<LocalFighter>) => onFightersChange(fighters.map((fighter) => fighter.id === id ? { ...fighter, ...patch } : fighter));
  const toggleFighter = (id: string) => {
    const currentIndex = selected.indexOf(id);
    if (currentIndex >= 0) setSelected([...selected.slice(0, currentIndex), ...selected.slice(currentIndex + 1)]);
    else if (selected.length < playerCount) setSelected([...selected, id]);
  };

  return (
    <OriginalDialog title={t(mode === "select" ? "players.loadTitle" : "players.title")} onClose={onClose} wide>
      <p className="player-editor-help">{t(mode === "select" ? "players.loadHelp" : "players.editHelp")}</p>
      <div className={mode === "select" ? "fighter-selection-grid" : "player-editor-grid"}>
        {fighters.map((fighter) => {
          const slot = selected.indexOf(fighter.id) + 1;
          if (mode === "select") return (
            <button key={fighter.id} type="button" className={`fighter-select-card${slot ? " selected" : ""}`} onClick={() => toggleFighter(fighter.id)}>
              <img src={`/assets/original/sprites/spr_player_${fighter.skin}_standing/0.png`} alt="" style={{ filter: `drop-shadow(0 0 5px ${cssColor(fighter.color)})` }} />
              <strong>{fighter.name}</strong>
              <span>{t("players.score")}: {fighter.score}</span>
              <b>{slot ? `${t("players.slot")} ${slot}` : t("players.notSelected")}</b>
            </button>
          );
          return (
            <fieldset key={fighter.id} className="player-card">
              <legend>{fighter.name}</legend>
              <img src={`/assets/original/sprites/spr_player_${fighter.skin}_standing/0.png`} alt="" style={{ filter: `drop-shadow(0 0 5px ${cssColor(fighter.color)})` }} />
              <label>{t("players.name")}<input value={fighter.name} maxLength={24} onChange={(event) => updateFighter(fighter.id, { name: event.target.value })} /></label>
              <label>{t("players.color")}<input type="color" value={cssColor(fighter.color)} onChange={(event) => updateFighter(fighter.id, { color: gmColor(event.target.value) })} /></label>
              <label>{t("players.skin")}<select value={fighter.skin} onChange={(event) => updateFighter(fighter.id, { skin: Number(event.target.value) })}>{[1, 2, 3, 4].map((skin) => <option key={skin} value={skin}>{skin}</option>)}</select></label>
              <span className="fighter-score">{t("players.score")}: <strong>{fighter.score}</strong></span>
            </fieldset>
          );
        })}
      </div>
      {mode === "select" ? (
        <div className="player-editor-actions"><button type="button" className="original-text-button" disabled={selected.length !== playerCount} onClick={() => {
          const remainder = [...selection, ...fighters.map(({ id }) => id)].filter((id) => !selected.includes(id));
          onSelectionChange([...selected, ...remainder].slice(0, 4));
          onClose();
        }}>{t("common.save")}</button></div>
      ) : (
        <div className="player-editor-actions">
          <button type="button" className="original-text-button" onClick={() => onFightersChange(fighters.map((fighter) => ({ ...fighter, score: 0 })))}>{t("players.resetScores")}</button>
        </div>
      )}
    </OriginalDialog>
  );
};
